import api from './api';
import { saveBlob } from '../utils/saveFile';

/**
 * Sage uploads, exports and Confirm — the API behind the Accounting Dashboard's
 * "Sage Uploads & Exports" tile, plus the Sage Export Details report.
 *
 * An export returns a SUMMARY, not the file: the AR run is 13,000 lines and the bytes
 * live in blob storage, so the file is fetched separately and can be fetched again from
 * Confirm later. `errors` non-empty means NOTHING was written — V1 refuses to deliver a
 * file with an uncodeable line in it, and so does this.
 */
export type SageFamily = 'invoices' | 'driverpay' | 'rebills' | 'addlcost';

export interface SageExportResult {
  recno: number;
  filename: string;
  recordcount: number;
  /** A decimal string — money never goes through a JS number on the way to a screen. */
  total: string;
  errors: string[];
  empty: boolean;
  records: { orders: number; extra: number; storage: number };
}

export interface OutstandingExport {
  recno: number;
  fileid: string;
  exportdatetime: string;
  recordcount: number;
  ttlamount: string;
}

export interface SageExportRow {
  recno: number;
  fileid: string;
  exportdatetime: string;
  confirmdatetime: string | null;
  recordcount: number;
  ttlamount: string;
  deleted: string;
  family: SageFamily;
  confirmed: boolean;
}

export interface SageExportDetail extends SageExportRow {
  recordLabel: string;
  records: string[];
  /** Why Mark Confirmed is unavailable — records the AR export covered that are not invoiced. */
  blockers: string[];
  canConfirm: boolean;
  canSaveChanges: boolean;
}

export interface CodChange { pronumber: number; csr: string; billto: string }

export interface BillToUploadResult {
  created: string[];
  updated: string[];
  unchanged: number;
  rows: number;
  /** The cascade the screen's name does not suggest: orders re-flagged COD (#22179). */
  codHeld: CodChange[];
  codReleased: CodChange[];
  notified: string[];
}

export interface VendorUploadResult {
  created: string[];
  updated: string[];
  unchanged: number;
  rows: number;
}

export interface SageDetailsRow {
  invoicedate: string | null;
  mas90code: string | null;
  cname: string | null;
  invoicenum: string | null;
  invoicesubtotal: string | null;
  invoicetotal: string | null;
  glcode: string | null;
  description: string | null;
}

export interface SageDetailsResponse {
  rows: SageDetailsRow[];
  truncated: boolean;
  includedLegacy: boolean;
}

export interface SageDetailsCriteria {
  start?: string;
  end?: string;
  billto?: number;
  searchby?: 'INVOICE #' | 'GL CODE';
  detaillist?: string;
  include_legacy?: boolean;
}

const base = '/sage';
const MULTIPART = { headers: { 'Content-Type': 'multipart/form-data' } };

const sageService = {
  // --- Uploads ---------------------------------------------------------------
  getUploadStatus: async (): Promise<{ billtos: string | null; vendors: string | null }> =>
    (await api.get(`${base}/uploads`)).data,

  // The Content-Type has to be passed even though the body is FormData: the api instance
  // sets a global default of application/json, and axios will not replace a header the
  // caller already specified, so the request goes out with no multipart boundary and
  // FastAPI reports the file field as simply missing. Every other upload in the app does
  // the same thing (proDocuments, quoteDocuments, fuelPumpPricing, orderUpdateImport).
  uploadBillTos: async (file: File): Promise<BillToUploadResult> => {
    const form = new FormData();
    form.append('file', file);
    return (await api.post<BillToUploadResult>(`${base}/uploads/billtos`, form, MULTIPART)).data;
  },

  uploadVendors: async (file: File): Promise<VendorUploadResult> => {
    const form = new FormData();
    form.append('file', file);
    return (await api.post<VendorUploadResult>(`${base}/uploads/vendors`, form, MULTIPART)).data;
  },

  // --- Exports ---------------------------------------------------------------
  /** Exports of this family produced but never confirmed — their records are still
   *  eligible, so exporting again would cover them a second time. */
  getOutstanding: async (family: SageFamily): Promise<OutstandingExport[]> =>
    (await api.get<{ outstanding: OutstandingExport[] }>(`${base}/exports/outstanding`, { params: { family } })).data.outstanding,

  runExport: async (family: SageFamily, enddate: string, costtypes: string[] = []): Promise<SageExportResult> =>
    (await api.post<SageExportResult>(`${base}/exports/${family}`, { enddate, costtypes })).data,

  // --- Confirm ---------------------------------------------------------------
  listExports: async (includeDeleted = false): Promise<SageExportRow[]> =>
    (await api.get<{ rows: SageExportRow[] }>(`${base}/exports`, { params: { include_deleted: includeDeleted } })).data.rows,

  getExport: async (recno: number): Promise<SageExportDetail> =>
    (await api.get<SageExportDetail>(`${base}/exports/${recno}`)).data,

  markConfirmed: async (recno: number): Promise<{ confirmed: boolean; family: SageFamily }> =>
    (await api.post(`${base}/exports/${recno}/confirm`)).data,

  setDeleted: async (recno: number, deleted: 'Y' | 'N'): Promise<{ changed: boolean }> =>
    (await api.patch(`${base}/exports/${recno}`, { deleted })).data,

  /** The exact bytes that went to Sage. Returns null on a cancelled save, so the page
   *  never reports a download that did not happen. */
  downloadExport: async (recno: number, fallbackName: string): Promise<string | null> => {
    const response = await api.get<Blob>(`${base}/exports/${recno}/file`, { responseType: 'blob' });
    const disposition = String(response.headers['content-disposition'] || '');
    const filename = /filename="([^"]+)"/i.exec(disposition)?.[1] || fallbackName;
    return await saveBlob(response.data, filename) ? filename : null;
  },

  // --- The report ------------------------------------------------------------
  getExportDetails: async (params: SageDetailsCriteria): Promise<SageDetailsResponse> =>
    (await api.get<SageDetailsResponse>('/accounting-reports/sage-export-details', { params })).data,
};

export default sageService;
